"use client";

import { Skeleton } from "@offstreetparking/design-system/feedback";
import { SelectionLayout } from "@offstreetparking/portal-flow/components/SelectionLayout";
import { TenantSelection } from "@offstreetparking/portal-flow/components/TenantSelection";
import { ParkingDetailsSkeleton } from "@offstreetparking/portal-flow/components/dataDisplay/parkingDetails";
import { ContactSkeleton } from "@offstreetparking/portal-flow/components/layouts/Contact";
import { ContentCard } from "@offstreetparking/portal-flow/components/layouts/ContentCard";
import { useRouter } from "next/navigation";
import { use, useEffect } from "react";
import { CommonLayout } from "~/components/layouts/CommonLayout";
import { CompanyScope } from "~/components/providers/CompanyProvider";
import { useFeatureFlags } from "~/hooks/useFeatureFlags";
import { routes } from "~/routes";
import { offstreetClient } from "~/services/offstreetApi";
import { usePortalV2LocationByIdentifier } from "~/services/portalV2Api";
import { mergeTenants } from "~/utils/merge-tenants";

type LocationIdentifierPageProps = {
	params: Promise<{ locale: string; locationIdentifier: string }>;
};

export default function LocationIdentifierPage(props: LocationIdentifierPageProps) {
	const params = use(props.params);
	const { locationIdentifier, locale } = params;
	const router = useRouter();
	const { isPending: flagsPending } = useFeatureFlags();

	const { data: p2Location, isPending: p2Pending } = usePortalV2LocationByIdentifier({
		identifier: locationIdentifier,
	});

	const { data: p3Data, isPending: p3Pending } = offstreetClient.locationLookup.getPermitTypesForLocation.useQuery(
		p2Location?.id ? { locationId: p2Location.id } : { identifier: locationIdentifier },
		{ enabled: !!p2Location?.id || !p2Pending },
	);

	const isLoading = flagsPending || p2Pending || p3Pending;

	const mergedTenants =
		!p2Pending && !p3Pending
			? mergeTenants({
					p3Tenants: p3Data?.tenants ?? [],
					p2Tenants: p2Location?.tenants ?? [],
					p2RegSettings: p2Location?.registrationSettings ?? [],
				})
			: [];
	const singleTenant = mergedTenants.length === 1 ? mergedTenants[0] : undefined;

	useEffect(() => {
		if (!singleTenant) {
			return;
		}

		router.replace(
			`/${locale}${routes.locationTenant({ identifier: locationIdentifier, tenantId: singleTenant.id })}`,
		);
	}, [singleTenant, router, locale, locationIdentifier]);

	if (isLoading) {
		return (
			<CommonLayout
				infoSlot={
					<div className="space-y-12">
						<div className="space-y-4">
							<Skeleton height={60} width={450} />
							<Skeleton height={60} width={225} />
						</div>

						<ContactSkeleton />
					</div>
				}
				isLoading={true}
			>
				<ContentCard>
					<ParkingDetailsSkeleton />
					<div className="border-neutral-faded border-t p-8 xl:hidden">
						<ContactSkeleton />
					</div>
				</ContentCard>
			</CommonLayout>
		);
	}

	if (singleTenant) {
		return null;
	}

	return (
		<SelectionLayout
			location={p3Data?.location ?? (p2Location ? { id: p2Location.id, name: p2Location.name } : undefined)}
			selectedTenant={undefined}
			branding={p3Data?.branding}
			support={p3Data?.support}
			contact={p3Data?.contact}
			registrationInstructions={p3Data?.locationInstructions?.registrationInstructions ?? undefined}
		>
			{p3Data?.company && <CompanyScope company={p3Data.company} />}
			<TenantSelection
				tenants={mergedTenants}
				onChange={(tenantId) => {
					router.push(
						`/${locale}${routes.locationTenant({ identifier: locationIdentifier, tenantId })}?tenantEditable=1`,
					);
				}}
			/>
		</SelectionLayout>
	);
}
